import { ChartCardTitle } from '@frontend/chart/components';
import { Spinner } from '@frontend/components/common';
import { ExportModal } from '@frontend/components/modal';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import OverviewHeader from '../../../applications/header/overview-header';
import { InsightDetailModeContext } from '../insight-detail-mode.context';
import { getInsightDefinition } from '../insight-registry/insight-registry';
import { buildInsightExportData } from './build-insight-export-data';
import { InsightDetailFilterBar } from './insight-detail-filter-bar.component';
import { InsightDetailFiltersContext } from './insight-detail-filters.context';
import { useInsightDetail } from './insight-detail.controller';
import { useInsightDetailFiltersState } from './use-insight-detail-filters-state';

const ID = 'insight-detail';
const EXPORT_MODAL_ID = 'insight-detail-export-modal';

const InsightDetail = () => {
    const { insightId } = useParams<{ insightId: string }>();
    const definition = getInsightDefinition(insightId);
    const { accountId, isReady, report } = useInsightDetail(definition?.reportType);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { openModal } = useModals();
    const filters = useInsightDetailFiltersState(definition?.filters);

    const onBack = () => {
        const next = new URLSearchParams(searchParams);
        // Drop filter params when returning to the overview grid.
        next.delete('after');
        next.delete('before');
        next.delete('bin_size');
        next.delete('spot_id');
        next.delete('product_id');
        next.delete('user_id');
        next.delete('type');
        next.delete('brand');
        if (definition?.tab) {
            next.set('insight-tag', definition.tab);
        }
        navigate({ pathname: '/analytics/insights', search: next.toString() });
    };

    const onExport = () => {
        if (!report) return;
        const data = buildInsightExportData(filters);
        openModal(EXPORT_MODAL_ID, () => (
            <ExportModal
                modalId={EXPORT_MODAL_ID}
                report={report}
                data={data}
            />
        ));
    };

    if (!definition) {
        return (
            <div
                className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'
                id={ID}>
                <OverviewHeader
                    title={
                        <FormattedMessage
                            id='platform.analytics.insights.insight-detail.not-found.title'
                            description='Title shown when an insight detail route does not match a known insight'
                            defaultMessage='Insight not found'
                        />
                    }
                    onBack={() => navigate({ pathname: '/analytics/insights', search: searchParams.toString() })}
                />
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NOT_FOUND}</div>
            </div>
        );
    }

    const DetailContent = definition.DetailContent;

    return (
        <div
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'
            id={ID}>
            <OverviewHeader
                title={
                    <ChartCardTitle
                        title={definition.title}
                        chartKey={definition.chartKey}
                        infoKind={definition.infoKind}
                        titleClassName='text-foreground-default justify-start text-xl font-semibold md:text-3xl'
                    />
                }
                onBack={onBack}
                actions={
                    report
                        ? [
                              <div
                                  key='export'
                                  onClick={onExport}
                                  className='bg-background-surface hover:bg-background-default outline-border-default flex h-11 w-fit cursor-pointer items-center justify-start gap-2 rounded-md px-4 py-3 outline-1 outline-offset-[-1px]'>
                                  <div className='text-brand-muted justify-start text-base font-medium text-nowrap'>{CommonMessage.BUTTONS.EXPORT}</div>
                              </div>
                          ]
                        : undefined
                }
            />
            {!isReady || !accountId ? (
                <Spinner />
            ) : (
                <InsightDetailModeContext.Provider value={true}>
                    <InsightDetailFiltersContext.Provider value={filters}>
                        <div className='flex w-full min-w-0 flex-1 flex-col gap-4'>
                            <InsightDetailFilterBar />
                            <DetailContent accountId={accountId} />
                        </div>
                    </InsightDetailFiltersContext.Provider>
                </InsightDetailModeContext.Provider>
            )}
        </div>
    );
};

export default InsightDetail;
